import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";
import styled from "styled-components";

import DetailsTemplate from "@client/components/templates/DetailsTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import Button from "@client/components/ui/Button";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import Table from "@client/components/ui/Table";
import licenceStore from "@client/stores/LicenceStore";
import licensorStore from "@client/stores/LicensorStore";
import roleStore from "@client/stores/RoleStore";
import Style from "@client/utils/Style";
import { Licensor } from "@typings/Licensor";

import LicensorModal from "../LicensorModal";
import DetailsFields from "./DetailsFields";
import licensorActionBarImage from "./images/user-action-bar.svg";

const Wrapper = styled.div`
  height: 100%;
`;
const Content = styled.div``;
const Buttons = styled.div`
  margin-top: 52px;
  display: flex;
  justify-content: space-between;
`;
const LinkStyled = styled(Link)`
  color: ${Style.palette.primary};
  text-decoration: none;
`;

type State = {
  showDeleteAlert: boolean;
  showLicensorModal: boolean;
  showDeleteErrorAlert: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
  id: string;
};

@observer
class LicensorDetailsPage extends React.Component<Props, State> {
  state: State = {
    showDeleteAlert: false,
    showLicensorModal: false,
    showDeleteErrorAlert: false,
  };

  get licensor(): Licensor | undefined {
    const id: string = this.props.id;
    return licensorStore.licensors.find(u => u.id === id);
  }

  get roleName(): string | null {
    let role: string | null = null;
    const licensor = this.licensor;
    if (licensor) {
      const projectId = licensorStore.tokenInfo?.project.id;
      role =
        roleStore.roles.find(r =>
          roleStore.roleAssignments.find(
            ra =>
              ra.role.name === r &&
              ra.user.id === licensor!.identity_id &&
              ra.scope?.project?.id === projectId,
          ),
        ) || null;
    }
    return role;
  }

  componentDidMount() {
    licensorStore.load();
    licenceStore.load();
    roleStore.load();
    roleStore.loadRoleAssignments();
  }

  handleCheckDelete() {
    this.setState({ showDeleteAlert: true });
  }

  async handleDeleteLicensor() {
    this.setState({ showDeleteAlert: false });
    if (!this.licensor) {
      return;
    }
    await licensorStore.delete(this.licensor.id);
    this.props.onNavigate("/licensors");
  }

  renderLicencesTable() {
    const licensor = this.licensor;
    if (!licensor) {
      return null;
    }

    const items = licenceStore.licences
      .filter(l => l.created_by === licensor!.email)
      .map(licence => [
        <LinkStyled key={licence.id} to={`/licences/${licence.id}`}>
          {licence.name || licence.id}
        </LinkStyled>,
        moment(licence.created_at).format("DD MMM YYYY HH:mm"),
        `${licence.migrations} / ${licence.replicas}`,
        licence.start_date
          ? moment(licence.start_date).format("DD MMM YYYY HH:mm")
          : "-",
        licence.end_date
          ? moment(licence.end_date).format("DD MMM YYYY HH:mm")
          : "-",
        licence.duration || "-",
      ]);
    return (
      <Table
        header={[
          "Licences",
          "Created At",
          "Migrations / Replicas",
          "Start Date",
          "End Date",
          "Duration",
        ]}
        noItemsLabel="No licences created by this licensor!"
        items={items}
        useSecondaryStyle
        style={{ marginTop: "32px" }}
      />
    );
  }

  renderContent() {
    return (
      <Content>
        <DetailsFields licensor={this.licensor || null} role={this.roleName} />
        {this.renderLicencesTable()}
        <Buttons>
          <Button
            primary
            onClick={() => {
              this.setState({ showLicensorModal: true });
            }}
          >
            Edit Licence Manager
          </Button>
        </Buttons>
      </Content>
    );
  }

  render() {
    const actions: DropdownActionType[] = [
      {
        label: "Edit Licence Manager",
        color: Style.palette.primary,
        action: () => {
          this.setState({ showLicensorModal: true });
        },
      },
      {
        label: "Delete Licence Manager",
        color: Style.palette.alert,
        action: () => {
          this.handleCheckDelete();
        },
      },
    ];

    return (
      <Wrapper>
        <DetailsTemplate
          contentComponent={this.renderContent()}
          backLink="/licensors"
          dropdownActions={actions}
          icon={licensorActionBarImage}
          title={this.licensor ? this.licensor.name : ""}
          subtitle={(this.licensor && this.licensor.description) || null}
        />
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Licence Manager"
            message="Are you sure you want to delete the licence manager?"
            extraMessage="Deleting a Coriolis Licence Manager is permanent!"
            onConfirmation={() => {
              this.handleDeleteLicensor();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showLicensorModal ? (
          <LicensorModal
            onRequestClose={() => {
              this.setState({ showLicensorModal: false });
            }}
            licensor={{ ...this.licensor!, role_name: this.roleName }}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Licensor"
            message="This licensor is the last licensor with the 'admin' role"
            extraMessage="You must first assign another licensor to the 'admin' role"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function LicensorDetailsPageWithNavigate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return <LicensorDetailsPage onNavigate={navigate} id={id!} />;
}

export default LicensorDetailsPageWithNavigate;
